/* Timezone helpers — IANA zones, browser detection and offset formatting. */

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Madrid', 'Europe/London', 'Europe/Lisbon', 'Europe/Paris',
  'Europe/Berlin', 'Europe/Rome', 'Atlantic/Canary',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Mexico_City', 'America/Bogota', 'America/Lima',
  'America/Argentina/Buenos_Aires', 'America/Sao_Paulo', 'America/Santiago',
];

/** Full list of IANA timezones supported by the browser. */
export function getTimezones(): string[] {
  try {
    const fn = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    if (fn) {
      const list = fn('timeZone');
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch {
    /* ignore — fall through to fallback */
  }
  return FALLBACK_TIMEZONES;
}

/** Best-guess timezone for the current browser. */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
  } catch {
    return 'Europe/Madrid';
  }
}

/** Current UTC offset for a zone, e.g. "GMT+2". */
export function timezoneOffset(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Human-friendly label, e.g. "Europe/Madrid · GMT+2". */
export function timezoneLabel(tz: string): string {
  const offset = timezoneOffset(tz);
  const name = tz.replace(/_/g, ' ');
  return offset ? `${name} · ${offset}` : name;
}

/* ── UTC ⇄ zoned-wall-clock conversion ───────────────────────
   A "zoned Date" is a Date whose UTC fields (getUTCHours, …) hold
   the wall-clock time of an instant as seen in a given timezone.
   This lets calendar maths run in plain, DST-free UTC space; the
   only timezone arithmetic happens at the conversion boundaries. */

/** Offset (ms) of `tz` at a given UTC instant. Positive = ahead of UTC. */
function tzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const v = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUTC = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour'), v('minute'), v('second'));
  return asUTC - utcMs;
}

/** A Date whose UTC fields hold the wall-clock of `instant` in `tz`. */
export function toZonedDate(instant: Date, tz: string): Date {
  return new Date(instant.getTime() + tzOffsetMs(instant.getTime(), tz));
}

/** Current wall-clock in `tz` as a zoned Date. */
export function nowZoned(tz: string): Date {
  return toZonedDate(new Date(), tz);
}

/** A zoned wall-clock (Y, M0-11, D, h, m) in `tz` → the real UTC Date. */
export function zonedToUtc(
  year: number, month0: number, day: number, hour: number, minute: number, tz: string,
): Date {
  const guess = Date.UTC(year, month0, day, hour, minute);
  const o1 = tzOffsetMs(guess, tz);
  let utc = guess - o1;
  const o2 = tzOffsetMs(utc, tz);
  if (o2 !== o1) utc = guess - o2; // second pass handles DST transitions
  return new Date(utc);
}

/** Format a zoned Date's UTC fields as "YYYY-MM-DDTHH:mm". */
export function zonedDateToInput(z: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${z.getUTCFullYear()}-${p(z.getUTCMonth() + 1)}-${p(z.getUTCDate())}T${p(z.getUTCHours())}:${p(z.getUTCMinutes())}`;
}

/** "YYYY-MM-DDTHH:mm" wall-clock in `tz` → UTC ISO string. */
export function zonedInputToIso(input: string, tz: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(input);
  if (!m) return new Date(input).toISOString();
  return zonedToUtc(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), tz).toISOString();
}

/** UTC ISO instant → "YYYY-MM-DDTHH:mm" wall-clock in `tz`. */
export function isoToZonedInput(iso: string, tz: string): string {
  return zonedDateToInput(toZonedDate(new Date(iso), tz));
}
